import React from "react";
import { View, StyleSheet, useWindowDimensions, Pressable } from "react-native";
import Text from "@components/CustomText";
import color from "@constants/color";

interface PayoutData {
  cxCount: number;
  incentive: number;
  total: number;
  category?: string;
}

interface CardProps {
  title: string;
  data: PayoutData[];
  setModalType?: (type: string, cta_api: string) => void;
  cta_api?: string;
  cta_txt?: string;
  selectedTab?: string;
}

interface TableRowProps {
  payload: {
    column1?: string | number;
    column2?: string | number;
    column3?: string | number;
    column4?: string | number;
    multiplySign: boolean;
    equalsToSign: boolean;
    isHeader?: boolean;
  };
  order: Array<
    "column1" | "column2" | "column3" | "column4" | "multiply" | "equals"
  >;
  row_extra_styles?: Record<string, any>;
  cell_extra_styles?: Record<string, any>;
}

const TableColumnOrders: {
  [key: string]: Array<
    "column1" | "column2" | "column3" | "column4" | "multiply" | "equals"
  >;
} = {
  BASE_PAYOUT: ["column1", "multiply", "column2", "equals", "column3"],
  WEIGHT_PAYOUT: [
    "column1",
    "column2",
    "multiply",
    "column3",
    "equals",
    "column4",
  ],
  SURGE_PAYOUT_WEEK: [
    "column1",
    "column2",
    "multiply",
    "column3",
    "equals",
    "column4",
  ],
  SURGE_PAYOUT: ["column1", "multiply", "column2", "equals", "column3"],
  ATTENDANCE_INCENTIVE: ["column1", "equals", "column2"],
  DELIVERY_INCENTIVE: ["column1", "multiply", "column2", "equals", "column3"],
};

const TableRow: React.FC<TableRowProps> = ({
  payload,
  order,
  row_extra_styles = {},
  cell_extra_styles = {},
}) => {
  const { isHeader } = payload;
  const { width } = useWindowDimensions();

  const renderCell = (type: string) => {
    switch (type) {
      case "multiply":
        return (
          <Text
            variant="caption_2"
            style={[
              styles.cell,
              styles.operatorCell,
              isHeader && styles.headerText,
              !isHeader && styles.hideOperatorCell,
              cell_extra_styles,
            ]}
          >
            ×
          </Text>
        );
      case "equals":
        return (
          <Text
            variant="caption_2"
            style={[
              styles.cell,
              styles.operatorCell,
              isHeader && styles.headerText,
              !isHeader && styles.hideOperatorCell,
              cell_extra_styles,
            ]}
          >
            =
          </Text>
        );
      default:
        return (
          (type === "column1" ||
            type === "column2" ||
            type === "column3" ||
            type === "column4") &&
          payload[type] && (
            <Text
              variant="caption_2"
              style={[
                styles.cell,
                isHeader && styles.headerText,
                isHeader && {
                  maxWidth: width * 0.25,
                },
                cell_extra_styles,
              ]}
            >
              {payload[type]}
            </Text>
          )
        );
    }
  };

  return (
    <View
      style={[
        styles.row,
        !isHeader ? { borderBottomWidth: 0 } : {},
        row_extra_styles,
      ]}
    >
      {order.map((cellType, index) => (
        <React.Fragment key={index}>{renderCell(cellType)}</React.Fragment>
      ))}
    </View>
  );
};

export const BasePayoutCard: React.FC<CardProps> = ({ title, data }) => (
  <View style={styles.cardWrapper}>
    <Text variant="caption_2" style={styles.cardTitle}>
      {title}
    </Text>
    <View style={styles.card}>
      <View style={styles.table}>
        <TableRow
          payload={{
            column1: "Delivered Customers",
            column2: "Per Customer Payout",
            column3: "Payout",
            isHeader: true,
            multiplySign: true,
            equalsToSign: true,
          }}
          order={TableColumnOrders.BASE_PAYOUT}
        />
        {data.map((item, index) => (
          <TableRow
            key={index}
            payload={{
              column1: item.cxCount,
              column2: `₹${item.incentive}`,
              column3: `₹${item.total}`,
              multiplySign: true,
              equalsToSign: true,
            }}
            order={TableColumnOrders.BASE_PAYOUT}
          />
        ))}
      </View>
    </View>
  </View>
);

export const WeightPayoutCard: React.FC<CardProps> = ({ title, data }) => (
  <View style={styles.cardWrapper}>
    <Text variant="caption_2" style={styles.cardTitle}>
      {title}
    </Text>
    <View style={styles.card}>
      <View style={styles.table}>
        <TableRow
          payload={{
            column1: "Weight",
            column2: "Incentive",
            column3: "Customer",
            column4: "Payout",
            isHeader: true,
            multiplySign: true,
            equalsToSign: true,
          }}
          order={TableColumnOrders.WEIGHT_PAYOUT}
        />
        {data.map((item, index) => (
          <TableRow
            key={index}
            payload={{
              column1: item.category || "",
              column2: `₹${item.incentive}`,
              column3: item.cxCount,
              column4: `₹${item.total}`,
              multiplySign: true,
              equalsToSign: true,
            }}
            order={TableColumnOrders.WEIGHT_PAYOUT}
          />
        ))}
      </View>
    </View>
  </View>
);

export const SurgePayoutCard: React.FC<CardProps> = ({
  title,
  data,
  selectedTab,
}) => (
  <View style={styles.cardWrapper}>
    <Text variant="caption_2" style={styles.cardTitle}>
      {title}
    </Text>
    <View style={styles.card}>
      <View style={styles.table}>
        <TableRow
          payload={{
            ...(selectedTab === "week"
              ? {
                  column1: "Date",
                  column2: "Del Customers",
                  column3: "Incentive",
                  column4: "Payout",
                }
              : {
                  column1: "Del Customers",
                  column2: "Incentive",
                  column3: "Payout",
                }),
            isHeader: true,
            multiplySign: true,
            equalsToSign: true,
          }}
          order={
            selectedTab === "week"
              ? TableColumnOrders.SURGE_PAYOUT_WEEK
              : TableColumnOrders.SURGE_PAYOUT
          }
        />
        {data.map((item, index) => (
          <TableRow
            key={index}
            payload={{
              ...(selectedTab === "week"
                ? {
                    column1: item.category,
                    column2: item.cxCount,
                    column3: `₹${item.incentive}`,
                    column4: `₹${item.total}`,
                  }
                : {
                    column1: item.cxCount,
                    column2: `₹${item.incentive}`,
                    column3: `₹${item.total}`,
                  }),
              multiplySign: true,
              equalsToSign: true,
            }}
            order={
              selectedTab === "week"
                ? TableColumnOrders.SURGE_PAYOUT_WEEK
                : TableColumnOrders.SURGE_PAYOUT
            }
          />
        ))}
      </View>
    </View>
  </View>
);

export const AttendanceIncentiveCard: React.FC<CardProps> = ({
  title,
  data,
  setModalType = () => {},
  cta_api = "",
  cta_txt = "",
}) => (
  <View style={styles.cardWrapper}>
    <View style={styles.cardTitleContainer}>
      <Text variant="caption_2" style={styles.cardTitle}>
        {title}
      </Text>
      {cta_txt ? (
        <Pressable
          onPress={() => setModalType("ATTENDANCE_INCENTIVE", cta_api)}
        >
          <Text variant="caption_2" style={styles.ctaTxt}>
            {cta_txt || "View Plan"}
          </Text>
        </Pressable>
      ) : (
        <View />
      )}
    </View>
    <View style={styles.card}>
      <View style={styles.table}>
        <TableRow
          payload={{
            column1: "Present Days",
            column2: "Payout",
            isHeader: true,
            multiplySign: true,
            equalsToSign: true,
          }}
          order={TableColumnOrders.ATTENDANCE_INCENTIVE}
        />
        {data.map((item, index) => (
          <TableRow
            key={index}
            payload={{
              column1: item.cxCount || "",
              column2: `₹${item.total}`,
              multiplySign: true,
              equalsToSign: true,
            }}
            order={TableColumnOrders.ATTENDANCE_INCENTIVE}
          />
        ))}
      </View>
    </View>
  </View>
);

export const DeliveryIncentiveCard: React.FC<CardProps> = ({
  title,
  data,
  setModalType = () => {},
  cta_api = "",
  cta_txt = "",
}) => (
  <View style={styles.cardWrapper}>
    <View style={styles.cardTitleContainer}>
      <Text variant="caption_2" style={styles.cardTitle}>
        {title}
      </Text>
      {cta_txt ? (
        <Pressable onPress={() => setModalType("DELIVERY_INCENTIVE", cta_api)}>
          <Text variant="caption_2" style={styles.ctaTxt}>
            {cta_txt || "View Plan"}
          </Text>
        </Pressable>
      ) : (
        <View />
      )}
    </View>
    <View style={styles.card}>
      <View style={styles.table}>
        <TableRow
          payload={{
            column1: "Delivered Customer",
            column2: "Incentive",
            column3: "Payout",
            isHeader: true,
            multiplySign: true,
            equalsToSign: true,
          }}
          order={TableColumnOrders.DELIVERY_INCENTIVE}
        />
        {data.map((item, index) => (
          <TableRow
            key={index}
            payload={{
              column1: item.cxCount || "",
              column2: `₹${item.incentive}`,
              column3: `₹${item.total}`,
              multiplySign: true,
              equalsToSign: true,
            }}
            order={TableColumnOrders.DELIVERY_INCENTIVE}
          />
        ))}
      </View>
    </View>
  </View>
);

export const ResultCard: React.FC<{
  title: string;
  subtitle?: string;
  value: string;
}> = ({ title, subtitle, value }) => (
  <View style={styles.resultCard}>
    <View style={styles.resultTitleContainer}>
      <Text variant="body_text_2" style={styles.resultValue}>
        {title}
      </Text>
      {subtitle && (
        <Text variant="body_text_2" style={styles.subtitle}>
          ({subtitle})
        </Text>
      )}
    </View>
    <Text variant="body_text_2" style={styles.resultValue}>
      {value}
    </Text>
  </View>
);

const styles = StyleSheet.create({
  cardWrapper: {
    marginBottom: 12,
  },
  card: {
    backgroundColor: "#F5F8FA",
    paddingHorizontal: 6,
    paddingVertical: 6,
    borderRadius: 8,
  },
  cardTitle: { marginVertical: 4 },
  cardTitleContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  ctaTxt: {
    color: color.blue,
    textDecorationLine: "underline",
  },
  table: {
    width: "100%",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-around",
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: color.white,
  },
  cell: {
    color: color.black,
    textAlign: "center",
  },
  operatorCell: {
    width: 14,
    textAlign: "center",
    color: color.black06,
    fontSize: 12,
  },
  headerText: {
    color: color.black06,
    textAlign: "left",
  },
  hideOperatorCell: {
    color: "#F5F8FA",
  },
  resultCard: {
    backgroundColor: "white",
    padding: 6,
    marginBottom: 2,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  resultTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  subtitle: {
    marginLeft: 8,
    color: color.black06,
  },
  resultValue: {
    color: color.black,
    fontWeight: "500",
  },
});
